import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { API_BASE_URL, apiFetch, GET } from "@/api/client"
import type { SpotTradePairResponse, TradePairsParams } from "@/api/types"
import { parseContentDispositionFilename } from "@/lib/export"

export function tradePairsOptions(params: TradePairsParams = {}) {
	return queryOptions({
		queryKey: ["trading", "pairs", params],
		queryFn: () =>
			GET("/api/v1/trading/pairs", {
				params: { query: params },
			}),
		placeholderData: keepPreviousData,
	})
}

export function useSpotTradePairs(params: TradePairsParams = {}) {
	return useQuery(tradePairsOptions(params))
}

export function openPositionTradesOptions(
	kind: "long" | "short",
	product: string,
) {
	return tradePairsOptions({ kind: [kind], product: [product], pageSize: 100 })
}

export function useOpenPositionTrades(
	kind: "long" | "short",
	product: string,
	enabled: boolean,
) {
	return useQuery({ ...openPositionTradesOptions(kind, product), enabled })
}

export function useSpotTradePair(
	tradeId: string | undefined,
	options: {
		placeholderData?: SpotTradePairResponse
		enabled?: boolean
	} = {},
) {
	const { placeholderData, enabled = true } = options
	return useQuery({
		queryKey: ["trading", "pair", tradeId],
		queryFn: () => {
			if (!tradeId) throw new Error("tradeId is required")
			return GET("/api/v1/trading/pairs/{trade_id}", {
				params: { path: { trade_id: tradeId } },
			})
		},
		enabled: !!tradeId && enabled,
		placeholderData,
	})
}

export async function exportTradesXlsx(
	year?: number,
): Promise<{ blob: Blob; filename: string }> {
	const params = year ? `?year=${year}` : ""
	const response = await apiFetch(
		`${API_BASE_URL}/api/v1/trading/export${params}`,
	)
	const disposition = response.headers.get("Content-Disposition") ?? ""
	const filename =
		parseContentDispositionFilename(disposition) ?? "hecla-trades.xlsx"
	return { blob: await response.blob(), filename }
}
